import {ChangeDetectionStrategy, Component, DestroyRef, inject, Input} from '@angular/core';
import {takeUntilDestroyed} from '@angular/core/rxjs-interop';
import {Store} from '@ngrx/store';
import {ExtendedFormControl} from '@aviary/forms/extensions/extended-form-control/extended-form-control';
import {ExtendedFormGroup} from '@aviary/forms/extensions/extended-form-group/extended-form-group';
import {InputFormFieldComponent} from '@aviary/forms/fields/input-form-field/input-form-field.component';
import {PhoneFormFieldComponent} from '@aviary/forms/fields/phone-form-field/phone-form-field.component';
import {RadioGroupFormFieldComponent} from '@aviary/forms/fields/radio-group-form-field/radio-group-form-field.component';
import {buildFormGroup, getExtendedControlFrom, updateEnablementFor} from '@aviary/forms/form.helpers';
import {isPopulatedString} from '@aviary/store/common/typing.helpers';
import {RegistrationActions} from '@store/registration/registration.actions';
import {FALLBACK_NOTIFICATION_PREFERENCES_VALUE} from '@store/registration/registration.fallback';
import {RegistrationSelectors} from '@store/registration/registration.selectors';
import {NOTIFICATION_OPTIONS} from '@store/registration/registration.types';
import {RegistrationValidators} from '@store/registration/registration.validators';
import {MatCard, MatCardContent} from '@angular/material/card';
import {NgOptimizedImage} from '@angular/common';
import {SmsTermsCheckboxComponent} from '@lib/components/sms-terms-checkbox/sms-terms-checkbox.component';
import {FilledButtonComponent} from '@aviary/buttons/filled-button/filled-button.component';

@Component({
  selector: 'px-registration-notifications-form',
  host: {
    class: 'flex flex-row justify-center lg:justify-between lg:pl-10 lg:pr-20'
  },
  templateUrl: './registration-notifications-form.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    InputFormFieldComponent, PhoneFormFieldComponent, RadioGroupFormFieldComponent, MatCard, MatCardContent, SmsTermsCheckboxComponent, NgOptimizedImage,
    FilledButtonComponent
  ]
})
export class RegistrationNotificationsFormComponent {

  @Input({required: true}) set email(email: string) {
    this.emailFieldControl.patchValue(email);
  }

  public readonly selectors = RegistrationSelectors.Registration;
  public readonly store = inject(Store);
  public readonly notificationsForm: ExtendedFormGroup = buildFormGroup(FALLBACK_NOTIFICATION_PREFERENCES_VALUE, inject(RegistrationValidators).setNotificationPrefs);
  private readonly emailFieldControl: ExtendedFormControl = getExtendedControlFrom(this.notificationsForm, 'notificationEmail');
  private readonly phoneFieldControl: ExtendedFormControl = getExtendedControlFrom(this.notificationsForm, 'mobilePhone');
  private readonly notifyMethodFieldControl: ExtendedFormControl = getExtendedControlFrom(this.notificationsForm, 'notifyMethod');
  private readonly smsTermsAcceptedFieldControl: ExtendedFormControl = getExtendedControlFrom(this.notificationsForm, 'smsTermsAccepted');
  private readonly destroyRef: DestroyRef = inject(DestroyRef);
  public readonly NOTIFICATION_OPTIONS = NOTIFICATION_OPTIONS;
  public readonly isPopulatedString = isPopulatedString;

  constructor() {
    this.updatePhoneRelevantFields()
    this.phoneFieldControl.valueChanges
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => this.updatePhoneRelevantFields());
  }

  public onClickNext() {
    const value = this.notificationsForm.value;
    const hasValidPhone = this.hasValidPhone();
    this.store.dispatch(RegistrationActions.updateRegistration({
      notificationEmail: value.notificationEmail,
      mobilePhone: value.mobilePhone,
      smsTermsAccepted: value.smsTermsAccepted ?? false,
      notifyText: hasValidPhone && value.notifyMethod === 'TEXT',
      notifyEmail: (!hasValidPhone) || value.notifyMethod === 'EMAIL',
    }));
    this.store.dispatch(RegistrationActions.completeRegistrationStep('setNotifications'));
  }

  public hasValidPhone(): boolean {
    return isPopulatedString(this.phoneFieldControl.value) && this.phoneFieldControl.valid;
  }

  private updatePhoneRelevantFields(): void {
    updateEnablementFor(this.notifyMethodFieldControl, this.hasValidPhone());
    updateEnablementFor(this.smsTermsAcceptedFieldControl, this.hasValidPhone())
  }
}
